import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ------- Auth -------
const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

// Session lives in an httpOnly cookie (bl_member) set by the API; nothing auth-related touches localStorage.
const authApi = axios.create({ baseURL: API, withCredentials: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Returning from Emergent Google OAuth: AuthCallback exchanges the session_id first, so skip the /me probe.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    authApi.get("/auth/me")
      .then(r => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
    // Run once on mount — authApi is a stable module singleton, no reactive deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await authApi.post("/auth/login", { email, password });
    setUser(r.data.user);
    return r.data.user;
  }, []);
  const register = useCallback(async (email, password, full_name) => {
    const r = await authApi.post("/auth/register", { email, password, full_name });
    setUser(r.data.user);
    return r.data.user;
  }, []);
  const logout = useCallback(async () => {
    try { await authApi.post("/auth/logout", {}); } catch { /* cookie may already be gone */ }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, login, register, logout, authApi }),
    [user, loading, login, register, logout],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

// ------- Cart -------
const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

const CART_KEY = "bl_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY) || "[]"); } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(items)); }, [items]);

  const add = useCallback((product, variant, qty = 1) => {
    setItems(prev => {
      const key = `${product.id}::${variant.id}`;
      const idx = prev.findIndex(x => `${x.product_id}::${x.variant_id}` === key);
      if (idx >= 0) {
        const copy = [...prev]; copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + qty };
        return copy;
      }
      return [...prev, {
        product_id: product.id, variant_id: variant.id, name: product.name,
        strength: variant.strength, unit_price: variant.price, quantity: qty,
        image: product.image || null, slug: product.slug,
        unit_label: variant.unit_label || product.unit_label || "10-vial pack", vials_per_unit: variant.vials ?? product.vials_per_unit ?? 10,
      }];
    });
    setOpen(true);
  }, []);

  const update = useCallback((product_id, variant_id, qty) => {
    setItems(prev => prev.map(x => (x.product_id === product_id && x.variant_id === variant_id)
      ? { ...x, quantity: Math.max(1, qty) } : x));
  }, []);
  const remove = useCallback((product_id, variant_id) => {
    setItems(prev => prev.filter(x => !(x.product_id === product_id && x.variant_id === variant_id)));
  }, []);
  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, x) => s + x.quantity, 0);
  const subtotal = items.reduce((s, x) => s + x.quantity * x.unit_price, 0);

  const value = useMemo(
    () => ({ items, add, update, remove, clear, count, subtotal, open, setOpen }),
    [items, add, update, remove, clear, count, subtotal, open],
  );

  return (
    <CartCtx.Provider value={value}>
      {children}
    </CartCtx.Provider>
  );
}

export { API };
